import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { getDashboardPath } from "../../../utils/authRedirect";
import { buildClinicPortalUrl } from "../../../utils/portalHost";
import { hasAdminAccess } from "../../../utils/roles";
import L from "../../../constants/baysportLabels";
import { TRIAL_DAYS } from "../../../constants/trial";
import { trackTryClick } from "../../../analytics/metrikaGoals";
import { yandexMetrikaParams } from "../../../analytics/yandexMetrika";
import {
  APP_LANDING_PATH,
  APP_LANDING_PAGE_TITLE,
  APP_LANDING_PAGE_DESCRIPTION,
  APP_LANDING_PAGE_KEYWORDS,
  LANDING_OG_IMAGE,
  LANDING_OG_IMAGE_ALT,
  buildAppLandingJsonLd,
} from "../HomeLanding/landingSeo";
import { SITE_ORIGIN } from "../../../config/site";
import { LegalFooterLinks } from "../../Legal/LegalDocLink";
import LandingNav from "../HomeLanding/LandingNav";
import Seo from "../../Seo";
import PunchMatteImage from "./PunchMatteImage";
import PhoneMockupSections from "./PhoneMockupSections";
import ScrollSteps from "./ScrollSteps";
import ClientsNeed from "./ClientsNeed";
import OwnersNeed from "./OwnersNeed";
import s from "./createAppLanding.module.css";

const METRIKA_LANDING = "app";

function onTryClick() {
  trackTryClick({ landing: METRIKA_LANDING });
}

export default function CreateAppLanding() {
  const { isAuth, role, tenant } = useSelector((state) => state.auth);
  const hasClub = isAuth && tenant?.subdomain && hasAdminAccess(role);

  useEffect(() => {
    yandexMetrikaParams({
      landing: METRIKA_LANDING,
      landing_path: APP_LANDING_PATH,
    });
  }, []);

  return (
    <div className={s.page}>
      <Seo
        title={APP_LANDING_PAGE_TITLE}
        description={APP_LANDING_PAGE_DESCRIPTION}
        keywords={APP_LANDING_PAGE_KEYWORDS}
        canonicalUrl={`${SITE_ORIGIN}${APP_LANDING_PATH}`}
        ogImage={LANDING_OG_IMAGE}
        ogImageAlt={LANDING_OG_IMAGE_ALT}
        jsonLd={buildAppLandingJsonLd()}
      />

      {hasClub ? (
        <div className={s.sessionBar}>
          <span>
            Вы вошли в <strong>{tenant.name || tenant.subdomain}</strong>
          </span>
          <div className={s.sessionActions}>
            <a
              href={buildClinicPortalUrl(
                tenant.subdomain,
                getDashboardPath(role),
              )}
              className={`${s.btn} ${s.btnPrimary}`}
            >
              Открыть кабинет
            </a>
            <Link to="/registraciya?new=1" className={`${s.btn} ${s.btnGhost}`}>
              Новый клуб
            </Link>
          </div>
        </div>
      ) : null}

      <LandingNav
        mode="platform"
        brandTo={APP_LANDING_PATH}
        onTryClick={onTryClick}
      />

      <section className={s.hero}>
        <div className={s.heroCopy}>
          <h1 className={s.heroBrand}>{L.productName}</h1>
          <p className={s.heroTitle}>
            Приложение для клиентов вашего клуба
          </p>
          <p className={s.heroLead}>
            Расписание, оплаты и чат группы — под вашим брендом. Без разработки.
          </p>
          <div className={s.heroActions}>
            <Link
              to="/registraciya"
              className={`${s.btn} ${s.btnPrimary} ${s.btnLg}`}
              onClick={onTryClick}
            >
              Попробовать {TRIAL_DAYS} дней
            </Link>
          </div>
        </div>
        <div className={s.heroVisual}>
          <div className={s.heroPhoneWrap}>
            <PunchMatteImage
              src="/landing/mock-client-home.png"
              alt="Приложение ученика"
              className={s.heroPhone}
            />
          </div>
        </div>
      </section>

      <PhoneMockupSections />
      <ScrollSteps />
      <ClientsNeed />
      <OwnersNeed />

      <section className={s.cta}>
        <div className={s.ctaBox}>
          <h2 className={s.sectionTitle} style={{ marginBottom: 12 }}>
            CRM с душой вашего клуба
          </h2>
          <p className={s.sectionLead} style={{ margin: "0 auto" }}>
            Приложение для клиентов и кабинет для клуба в одном месте.
            {` ${TRIAL_DAYS} дней бесплатно на каждый новый клуб.`}
          </p>
          <div className={s.ctaActions}>
            <Link
              to="/registraciya"
              className={`${s.btn} ${s.btnPrimary} ${s.btnLg}`}
              onClick={onTryClick}
            >
              Попробовать бесплатно
            </Link>
            <Link to="/find" className={`${s.btn} ${s.btnOutline} ${s.btnLg}`}>
              Найти свой клуб
            </Link>
          </div>
        </div>
      </section>

      {!hasClub ? (
        <div className={s.mobileStickyCta}>
          <Link
            to="/registraciya"
            className={`${s.btn} ${s.btnPrimary}`}
            onClick={onTryClick}
          >
            {TRIAL_DAYS} дней бесплатно
          </Link>
        </div>
      ) : null}

      <footer className={s.footer}>
        <LegalFooterLinks />
      </footer>
    </div>
  );
}
